<template>
  <v-dialog
    v-model="dialog"
    max-width="800px"
    @click:outside="close"
  >
    <v-card>
      <v-card-title class="text-h5 grey lighten-2">
        {{ product.name }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-container>
        <v-row>
          <v-col cols="12" md="6">
            <v-img
              :src="product.image"
              height="400"
              contain
            ></v-img>
          </v-col>
          <v-col cols="12" md="6">
            <h3 class="text-h5 mb-2">{{ product.name }}</h3>
            
            <div class="d-flex align-center mb-3">
              <v-chip
                v-if="!product.inStock"
                color="red"
                text-color="white"
                small
                class="mr-2"
              >
                Esgotado
              </v-chip>
              <v-chip
                v-else
                color="green"
                text-color="white"
                small
                class="mr-2"
              >
                {{ getStockText() }}
              </v-chip>
              
              <v-chip
                v-if="product.isNewProduct"
                color="primary"
                text-color="white"
                small
              >
                Novo
              </v-chip>
              
              <!-- Rating display if available -->
              <div v-if="product.rating" class="ml-auto d-flex align-center">
                <v-rating
                  v-model="product.rating"
                  color="amber"
                  dense
                  half-increments
                  readonly
                  size="18"
                ></v-rating>
                <span class="text-caption grey--text ml-1">
                  {{ product.rating }}
                </span>
              </div>
            </div>
            
            <div class="text-h4 font-weight-bold primary--text mb-4">
              R$ {{ product.price.toFixed(2).replace('.', ',') }}
            </div>
            
            <v-divider class="mb-4"></v-divider>
            
            <p class="text-body-1 mb-4">{{ product.description }}</p>
            
            <!-- Cores (se disponíveis) -->
            <div v-if="product.colors && product.colors.length > 0" class="mb-4">
              <h4 class="text-subtitle-1 font-weight-bold mb-2">Cores</h4>
              <v-chip-group
                v-model="selectedColor"
                mandatory
              >
                <v-chip
                  v-for="color in product.colors"
                  :key="color"
                  filter
                  outlined
                >
                  {{ color }}
                </v-chip>
              </v-chip-group>
            </div>
            
            <!-- Opções (se disponíveis) -->
            <div v-if="product.options && product.options.length > 0" class="mb-4">
              <h4 class="text-subtitle-1 font-weight-bold mb-2">Opções</h4>
              <v-select
                v-model="selectedOption"
                :items="product.options"
                label="Selecione uma opção"
                outlined
                dense
                class="mb-4"
              ></v-select>
            </div>
            
            <!-- Tamanhos (se disponíveis) -->
            <div v-if="product.sizes && product.sizes.length > 0" class="mb-4">
              <h4 class="text-subtitle-1 font-weight-bold mb-2">Tamanho</h4>
              <v-chip-group
                v-model="selectedSize"
                mandatory
              >
                <v-chip
                  v-for="(size, index) in product.sizes"
                  :key="index"
                  filter
                  outlined
                >
                  {{ size }}
                </v-chip>
              </v-chip-group>
            </div>
            
            <!-- Quantidade -->
            <div class="mt-4">
              <h4 class="text-subtitle-1 font-weight-bold mb-2">Quantidade</h4>
              <div class="d-flex align-center">
                <v-btn
                  icon
                  @click="decrementQuantity"
                  :disabled="quantity <= 1"
                >
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
                <v-text-field
                  v-model="quantity"
                  type="number"
                  min="1"
                  outlined
                  dense
                  hide-details
                  style="width: 70px"
                  class="mx-2"
                ></v-text-field>
                <v-btn
                  icon
                  @click="incrementQuantity"
                  :disabled="!product.inStock"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </div>
            </div>
            
            <!-- Informação de entrega -->
            <v-alert
              dense
              text
              type="info"
              class="mt-4 mb-4"
            >
              <v-icon left small>mdi-truck-delivery</v-icon>
              Prazo de entrega estimado: {{ getDeliveryEstimate() }}
            </v-alert>
            
            <!-- Botão -->
            <v-btn
              color="primary"
              block
              x-large
              :disabled="!product.inStock"
              @click="addToCart"
            >
              <v-icon left>mdi-cart</v-icon>
              Adicionar ao carrinho
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ProductDetailModal',
  props: {
    product: {
      type: Object,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: this.show,
      selectedOption: null,
      selectedSize: null,
      selectedColor: null,
      quantity: 1
    };
  },
  watch: {
    show(newVal) {
      this.dialog = newVal;
      if (newVal) {
        // Reset selections for new product
        this.selectedOption = this.product.options ? this.product.options[0] : null;
        this.selectedSize = this.product.sizes ? 0 : null;
        this.selectedColor = this.product.colors ? this.product.colors[0] : null;
        this.quantity = 1;
      }
    },
    dialog(newVal) {
      if (!newVal) {
        this.$emit('close');
      }
    },
    product(newProduct) {
      // Reset selections for new product
      this.selectedOption = newProduct.options ? newProduct.options[0] : null;
      this.selectedSize = newProduct.sizes ? 0 : null;
      this.selectedColor = newProduct.colors ? newProduct.colors[0] : null;
      this.quantity = 1;
    }
  },
  methods: {
    close() {
      this.dialog = false;
    },
    incrementQuantity() {
      if (this.product.inStock) {
        this.quantity++;
      }
    },
    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    addToCart() {
      if (!this.product.inStock) return;
      
      const cartItem = {
        productId: this.product.id,
        name: this.product.name,
        price: this.product.price,
        quantity: this.quantity,
        option: this.selectedOption,
        size: this.selectedSize !== null && this.product.sizes ? this.product.sizes[this.selectedSize] : null,
        image: this.product.image,
        color: this.selectedColor || null
      };
      
      // Aqui você implementaria a lógica para adicionar ao carrinho
      // Por exemplo:
      // this.$store.dispatch('cart/addItem', cartItem);
      
      console.log('Adicionado ao carrinho:', cartItem);
      this.$toast.success('Produto adicionado ao carrinho!');
      this.close();
    },
    
    // Método para gerar texto de disponibilidade
    getStockText() {
      if (!this.product.inStock) {
        return 'Esgotado';
      }
      
      // Pode adicionar variações com base em dados fictícios
      const stockLevel = Math.floor(Math.random() * 100);
      if (stockLevel < 5) {
        return 'Últimas unidades!';
      } else if (stockLevel < 20) {
        return 'Poucas unidades disponíveis';
      } else {
        return 'Em estoque';
      }
    },
    
    // Método para gerar texto de tempo de entrega
    getDeliveryEstimate() {
      const baseTime = Math.floor(Math.random() * 3) + 3; // 3-5 dias
      const maxTime = baseTime + 2;
      return `${baseTime}-${maxTime} dias úteis`;
    }
  }
};
</script>